import { Colors } from '@be-brite/briteui';
import { CopyIcon, SpeakerHighIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { Box, Button, Text } from '../shared-components';
import { stripMarkdown } from './helpers/assistant';
import { AssistantMarkdownRenderer } from './AssistantMarkdownRenderer';

export const AssistantChatAssistantResponse = ({ message }) => {
  // State for controlling hover menu visibility and speech synthesis.
  const [isHovered, setIsHovered] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  /**
   * Copies the assistant's response text to the clipboard.
   * Strips markdown formatting to provide clean, readable plain text.
   */
  const handleCopyToClipboard = async () => {
    try {
      const plainText = stripMarkdown(message.text);
      await navigator.clipboard.writeText(plainText);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  /**
   * Handles text-to-speech functionality for the assistant's response.
   * Converts markdown to plain text for better speech synthesis.
   * Supports play/stop toggle functionality.
   */
  const handleTextToSpeech = () => {
    // Stop speech if currently playing.
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    // Convert markdown to plain text for natural speech.
    const plainText = stripMarkdown(message.text);
    const utterance = new SpeechSynthesisUtterance(plainText);

    // Clean up state when speech ends or encounters an error.
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  // Configuration for hover menu items - makes it easy to add/remove functionality.
  const menuItems = [
    {
      icon: SpeakerHighIcon,
      onClick: handleTextToSpeech,
    },
    {
      icon: CopyIcon,
      onClick: handleCopyToClipboard,
    },
  ];

  return (
    <Box
      css={`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0px 4px;
        gap: 8px;
        position: relative;
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Label for the assistant's response. */}
      <Text helper color={Colors.Gray400}>
        Answer
      </Text>

      {/* Renders the assistant's markdown response with proper formatting. */}
      <AssistantMarkdownRenderer message={message} />

      {/* Hover Menu - Always rendered to prevent layout shift when appearing/disappearing. */}
      <Box
        css={`
          display: flex;
          align-items: center;
          height: 24px; /* Fixed height prevents layout shift. */
          opacity: ${isHovered ? 1 : 0};
          visibility: ${isHovered ? 'visible' : 'hidden'};
          transition: opacity 0.2s ease, visibility 0.2s ease;
        `}
      >
        {/* Dynamically render menu items from configuration. */}
        {menuItems.map((item, index) => (
          <MenuItem key={index} icon={item.icon} onClick={item.onClick} isActive={item.isActive}>
            {item.children}
          </MenuItem>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Reusable menu item component for the hover menu.
 * Provides consistent styling and behavior for all menu buttons.
 *
 * @param {React.Component} icon - Phosphor icon component to display.
 * @param {Function} onClick - Click handler for the menu item.
 */
const MenuItem = ({ icon: Icon, onClick }) => (
  <Button
    styles="icon"
    css={`
      position: relative;
      padding: 0px;
      height: 24px;
      width: 24px;
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.05);
      }

      &:active {
        background-color: ${Colors.Gray600};
        transform: scale(0.95);
      }
    `}
    onClick={onClick}
  >
    <Icon size={16} color={Colors.Gray400} weight="bold" />
  </Button>
);
